import { useParams, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import StNavbar from '../../components/StNavbar';
import StFooter from '../../components/StFooter';
import { SetSeo } from '../../lib/common_funcs';

import '../../redesign-2025.css';
import AOS from 'aos';
import 'aos/dist/aos.css';

import { DocsConfig } from './DocsConfig';

function StDocPage() {
    const { docSlug } = useParams();
    const doc = DocsConfig[docSlug];

    const [loading, setLoading] = useState(true);
    const [htmlContent, setHtmlContent] = useState('');

    useEffect(() => {
        AOS.init({
            duration: 800,
            once: true,
            easing: 'ease-out-cubic',
            offset: 50
        });
    }, []);

    useEffect(() => {
        if (!doc || doc.external) {
            setLoading(false);
            return;
        }

        const fetchMarkdown = async () => {
            try {
                const response = await fetch(`/docs/${doc.mdFile}`);
                const mdText = await response.text();
                const html = markdownToHtml(mdText);
                setHtmlContent(html);
                setLoading(false);
            } catch (error) {
                console.error('Error loading documentation:', error);
                setLoading(false);
            }
        };

        fetchMarkdown();
    }, [doc]);

    if (!doc) {
        return <Navigate to="/not-found" replace />;
    }

    if (doc.external) {
        window.location.href = doc.url;
        return null;
    }

    const allParams = {
        "seo": doc.seo,
        "schema": doc.schema
    };

    if (loading) {
        return (
            <div className="ts-redesign">
                <SetSeo {...allParams} />
                <StNavbar />
                <div className="ts-page-hero" style={{minHeight: '300px'}}>
                    <div className="container text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3" style={{color: 'var(--ts-gray)'}}>Loading documentation...</p>
                    </div>
                </div>
                <StFooter />
            </div>
        );
    }

    return (
        <div className="ts-redesign">
            <SetSeo {...allParams} />
            <StNavbar />

            <header className="ts-blog-hero">
                <div className="ts-hero-bg">
                    <div className="ts-gradient-orb ts-orb-1"></div>
                    <div className="ts-gradient-orb ts-orb-2"></div>
                </div>
                <div className="container">
                    <div className="ts-blog-hero-content" data-aos="fade-up">
                        <span className="ts-blog-category">{doc.category}</span>
                        <h1 className="ts-blog-hero-title">{doc.name}</h1>
                        <p className="ts-blog-hero-subtitle">{doc.description}</p>
                    </div>
                </div>
            </header>

            <main className="main-content">
                <section className="ts-section ts-section-light">
                    <div className="container">
                        <div className="ts-blog-content" data-aos="fade-up">
                            <div className="ts-blog-body ts-doc-body" dangerouslySetInnerHTML={{__html: htmlContent}}></div>
                        </div>

                        <div className="ts-blog-cta" data-aos="fade-up">
                            <Link to="/docs" className="ts-btn ts-btn-glass">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                                Back to Documentation
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <StFooter />
        </div>
    );
}

function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function markdownToHtml(markdown) {
    const lines = markdown.split('\n');
    const result = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // Empty line
        if (trimmed === '') {
            result.push('');
            i++;
            continue;
        }

        // Horizontal rule
        if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
            result.push('<hr />');
            i++;
            continue;
        }

        // Code block
        if (trimmed.startsWith('```')) {
            const lang = trimmed.slice(3).trim();
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }
            i++; // Skip closing ```
            const code = escapeHtml(codeLines.join('\n'));
            result.push(`<pre><code class="language-${lang}">${code}</code></pre>`);
            continue;
        }

        // Headers
        const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
        if (headerMatch) {
            const level = headerMatch[1].length;
            const text = processInline(headerMatch[2]);
            result.push(`<h${level}>${text}</h${level}>`);
            i++;
            continue;
        }

        // Table
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            const tableLines = [];
            while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                tableLines.push(lines[i].trim());
                i++;
            }
            result.push(convertTable(tableLines));
            continue;
        }

        // Unordered list
        if (/^[-*]\s+/.test(trimmed)) {
            const listItems = [];
            while (i < lines.length) {
                const listLine = lines[i].trim();
                const match = listLine.match(/^[-*]\s+(.+)$/);
                if (match) {
                    listItems.push(processInline(match[1]));
                    i++;
                } else if (listLine === '') {
                    // Check if next non-empty line is also a list item
                    let j = i + 1;
                    while (j < lines.length && lines[j].trim() === '') j++;
                    if (j < lines.length && /^[-*]\s+/.test(lines[j].trim())) {
                        i++;
                        continue;
                    }
                    break;
                } else {
                    break;
                }
            }
            result.push('<ul>' + listItems.map(item => `<li>${item}</li>`).join('') + '</ul>');
            continue;
        }

        // Ordered list
        if (/^\d+\.\s+/.test(trimmed)) {
            const listItems = [];
            while (i < lines.length) {
                const listLine = lines[i].trim();
                const match = listLine.match(/^\d+\.\s+(.+)$/);
                if (match) {
                    listItems.push(processInline(match[1]));
                    i++;
                } else if (listLine === '') {
                    let j = i + 1;
                    while (j < lines.length && lines[j].trim() === '') j++;
                    if (j < lines.length && /^\d+\.\s+/.test(lines[j].trim())) {
                        i++;
                        continue;
                    }
                    break;
                } else {
                    break;
                }
            }
            result.push('<ol>' + listItems.map(item => `<li>${item}</li>`).join('') + '</ol>');
            continue;
        }

        // Blockquote
        if (trimmed.startsWith('>')) {
            const quoteLines = [];
            while (i < lines.length && lines[i].trim().startsWith('>')) {
                quoteLines.push(lines[i].trim().replace(/^>\s*/, ''));
                i++;
            }
            const quoteContent = quoteLines.map(l => processInline(l)).join('<br />');
            result.push(`<blockquote>${quoteContent}</blockquote>`);
            continue;
        }

        // Regular paragraph
        const paragraphLines = [];
        while (i < lines.length) {
            const pLine = lines[i].trim();
            // Stop at empty line or block elements
            if (pLine === '' ||
                pLine.startsWith('#') ||
                pLine.startsWith('```') ||
                pLine.startsWith('|') ||
                /^[-*]\s+/.test(pLine) ||
                /^\d+\.\s+/.test(pLine) ||
                /^-{3,}$/.test(pLine) ||
                pLine.startsWith('>')) {
                break;
            }
            paragraphLines.push(pLine);
            i++;
        }
        if (paragraphLines.length > 0) {
            const text = processInline(paragraphLines.join(' '));
            result.push(`<p>${text}</p>`);
        }
    }

    return result.join('\n');
}

function processInline(text) {
    // Process inline elements
    let result = text;

    // Inline code (must be processed first to protect content)
    const inlineCodes = [];
    result = result.replace(/`([^`]+)`/g, (match, code) => {
        const placeholder = `__ICODE_${inlineCodes.length}__`;
        inlineCodes.push(`<code>${escapeHtml(code)}</code>`);
        return placeholder;
    });

    // Escape remaining HTML entities
    result = escapeHtml(result);

    // Restore inline codes
    inlineCodes.forEach((code, idx) => {
        result = result.replace(`__ICODE_${idx}__`, code);
    });

    // Bold (must be before italic)
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    // Italic with underscores (safer than asterisks)
    result = result.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

    // Links
    result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Images
    result = result.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />');

    return result;
}

function convertTable(lines) {
    if (lines.length < 2) return '';

    let html = '<div class="table-responsive"><table class="ts-doc-table">';

    // Header row
    const headerCells = lines[0].split('|').slice(1, -1); // Remove first and last empty from split
    html += '<thead><tr>';
    headerCells.forEach(cell => {
        html += `<th>${processInline(cell.trim())}</th>`;
    });
    html += '</tr></thead>';

    // Skip separator line (index 1), process body rows
    html += '<tbody>';
    for (let i = 2; i < lines.length; i++) {
        const cells = lines[i].split('|').slice(1, -1);
        html += '<tr>';
        cells.forEach(cell => {
            html += `<td>${processInline(cell.trim())}</td>`;
        });
        html += '</tr>';
    }
    html += '</tbody></table></div>';

    return html;
}

export default StDocPage;
